import React, { useEffect, useState } from 'react';
import { setCookie, getCookie } from '../../../utils/cookieManager';
import './AfterLoginModal.scss';

export interface AfterLoginModalContent {
  title?: string;
  content: string;
  buttonLabel?: string;
  doNotShowLabel?: string;
}

interface AfterLoginModalProps {
  content: AfterLoginModalContent;
  onClose?: () => void;
  autoShow?: boolean;
}

const AfterLoginModal: React.FC<AfterLoginModalProps> = ({
  content,
  onClose,
  autoShow = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);

  useEffect(() => {
    // Check if user has dismissed this modal before
    const doNotShow = getCookie('do_not_show_again');
    
    if (autoShow && !doNotShow) {
      // Show modal after a short delay for smooth appearance
      setTimeout(() => {
        setIsVisible(true);
        document.body.classList.add('modal-open');
      }, 300);
    }
  }, [autoShow]);

  const handleClose = () => {
    if (doNotShowAgain) {
      // Set cookie following WordPress pattern: 365 days, now.com domain
      setCookie('do_not_show_again', '1', {
        days: 365,
        domain: 'now.com',
        path: '/',
        secure: false,
        sameSite: 'None'
      });
    }
    
    setIsVisible(false);
    document.body.classList.remove('modal-open');
    onClose?.();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking directly on the backdrop, not the modal content
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <div 
        className="modal-backdrop fade show"
        onClick={handleBackdropClick}
      />
      <div 
        className="modal fade show contact-update-popup"
        id="contact-update-popup"
        role="dialog"
        aria-modal="true"
        aria-labelledby="afterLoginModalTitle"
      >
        <div className="modal-dialog modal-md modal-dialog-centered">
          <div className="modal-content py-4 px-3 mt-4">
            <button 
              type="button" 
              className="close" 
              onClick={handleClose}
              aria-label="Close"
            >
              <span>Close</span>
              <img 
                src={`${window.location.origin}/wp-content/themes/wmx/images/icons/mw-remove.png`}
                alt="Close"
              />
            </button>
            
            <div className="modal-body text-center">
              {/* Render HTML content from WordPress */}
              <div 
                dangerouslySetInnerHTML={{ __html: content.content }}
                id="afterLoginModalTitle"
              />
            </div>
            
            {/* Modal footer with checkbox */}
            <div className="modal-footer sticky-bottom">
              <div className="dont-show-again-wrapper">
                <label className="dont-show-again">
                  <input
                    type="checkbox"
                    name="do_not_show_again"
                    id="donotshowagain"
                    value="1"
                    checked={doNotShowAgain}
                    onChange={(e) => setDoNotShowAgain(e.target.checked)}
                  />
                  <span>{content.doNotShowLabel || 'Do not show this message again'}</span>
                </label>
              </div>
            </div>
            
            <div className="modal-action">
              <button 
                type="button" 
                onClick={handleClose}
                className="default-btn thinner"
              >
                {content.buttonLabel || 'OK'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AfterLoginModal;
